import { AuthInteractionEndedReason } from '../interfaces/collection.interface';
import {
  AuthInteractionDto,
  CollectionRevisionConnectionTargetDto,
  ConnectionAcquisitionDto,
  ConnectionRuntimeDto,
} from '../interfaces/collection-revision-connection.interface';
import { CollectionRevisionWireContractError } from '../services/collection-revision.service';

/** Connection error code shown by the provider-setup screen. */
export const PROVIDER_SETUP_REQUIRED_CODE = 'PROVIDER_2FA_SETUP_REQUIRED';

export type ConnectionPresentationOrigin =
  | 'ROUTE_HYDRATION'
  | 'COMMAND_CONTINUATION';

export type ConnectionScreenDecision =
  | { readonly kind: 'ENTITY_SELECTION' }
  | {
      readonly kind: 'CREDENTIALS';
      readonly mode: 'INITIAL' | 'REAUTH' | 'FAILED_RETRY';
    }
  | {
      readonly kind: 'RESUME_OR_CANCEL';
      readonly connection: ConnectionRuntimeDto;
    }
  | { readonly kind: 'AUTH_INTERACTION'; readonly summary: AuthInteractionDto }
  | {
      readonly kind: 'AUTH_INTERACTION_ENDED';
      readonly reason: AuthInteractionEndedReason;
    }
  /** The provider account must first enroll two-factor on the provider's site. */
  | { readonly kind: 'PROVIDER_SETUP_REQUIRED' }
  | { readonly kind: 'ACTIVE_PROGRESS' }
  | {
      readonly kind: 'COMPLETED_READ_ONLY';
      readonly acquisition: ConnectionAcquisitionDto;
    };

export type ConnectionBackDecision =
  | { readonly kind: 'NAVIGATE_PREVIOUS_TARGET_OR_INTRO' }
  | {
      readonly kind: 'CONFIRM_CONNECTION_CANCELLATION';
      readonly connectionId: string;
    }
  | { readonly kind: 'BLOCKED' };

export class CollectionConnectionPresenter {
  present(
    runtime: CollectionRevisionConnectionTargetDto,
    origin: ConnectionPresentationOrigin,
  ): ConnectionScreenDecision {
    assertRuntimeShape(runtime);

    if (runtime.target_status === 'COMPLETED') {
      const acquisition = runtime.acquisition;
      if (!acquisition) {
        throw contractError(
          'A completed connection target requires its acquisition snapshot.',
        );
      }
      return {
        kind: 'COMPLETED_READ_ONLY',
        acquisition,
      };
    }

    const connection = runtime.connection;
    if (!connection) return { kind: 'ENTITY_SELECTION' };

    const lastErrorCode = connection.last_error?.code;
    switch (connection.status) {
      case 'NEEDS_CREDENTIALS': {
        if (lastErrorCode === PROVIDER_SETUP_REQUIRED_CODE) {
          return { kind: 'PROVIDER_SETUP_REQUIRED' };
        }
        // An ended verification stays on the two-factor screen.
        const reason = toAuthInteractionEndedReason(lastErrorCode);
        return reason
          ? { kind: 'AUTH_INTERACTION_ENDED', reason }
          : { kind: 'CREDENTIALS', mode: 'REAUTH' };
      }
      case 'FAILED':
        return lastErrorCode === PROVIDER_SETUP_REQUIRED_CODE
          ? { kind: 'PROVIDER_SETUP_REQUIRED' }
          : { kind: 'CREDENTIALS', mode: 'FAILED_RETRY' };
      case 'EXPIRED':
        return { kind: 'CREDENTIALS', mode: 'REAUTH' };
      case 'AWAITING_AUTH_INTERACTION':
        if (!connection.auth_interaction) {
          throw contractError(
            'An awaiting-auth connection requires its interaction summary.',
          );
        }
        return origin === 'ROUTE_HYDRATION'
          ? { kind: 'RESUME_OR_CANCEL', connection }
          : {
              kind: 'AUTH_INTERACTION',
              summary: connection.auth_interaction,
            };
      case 'PENDING':
      case 'AUTHENTICATING':
      case 'ACTIVE':
        return origin === 'ROUTE_HYDRATION'
          ? { kind: 'RESUME_OR_CANCEL', connection }
          : { kind: 'ACTIVE_PROGRESS' };
    }
  }

  decideBack(
    runtime: CollectionRevisionConnectionTargetDto,
    screen: ConnectionScreenDecision,
    busy: boolean,
  ): ConnectionBackDecision {
    if (busy) return { kind: 'BLOCKED' };
    if (
      runtime.connection &&
      (screen.kind === 'CREDENTIALS' ||
        screen.kind === 'AUTH_INTERACTION' ||
        screen.kind === 'PROVIDER_SETUP_REQUIRED')
    ) {
      return {
        kind: 'CONFIRM_CONNECTION_CANCELLATION',
        connectionId: runtime.connection.connection_id,
      };
    }
    return { kind: 'NAVIGATE_PREVIOUS_TARGET_OR_INTRO' };
  }
}

function assertRuntimeShape(
  runtime: CollectionRevisionConnectionTargetDto,
): void {
  if (runtime.target_status === 'COMPLETED') {
    if (runtime.connection || !runtime.acquisition) {
      throw contractError(
        'A completed connection target requires only its acquisition snapshot.',
      );
    }
    return;
  }
  if (runtime.acquisition) {
    throw contractError(
      'A noncompleted connection target cannot expose an acquisition snapshot.',
    );
  }
  if (runtime.target_status === 'PENDING') {
    if (runtime.connection) {
      throw contractError('A pending target cannot expose a live connection.');
    }
    return;
  }
  const connection = runtime.connection;
  if (!connection) {
    throw contractError('An in-progress target requires a live connection.');
  }
  const awaitingInteraction =
    connection.status === 'AWAITING_AUTH_INTERACTION';
  if (awaitingInteraction !== (connection.auth_interaction !== null)) {
    throw contractError(
      'The auth-interaction summary contradicts the connection status.',
    );
  }
}

function toAuthInteractionEndedReason(
  code: string | undefined,
): AuthInteractionEndedReason | null {
  switch (code) {
    case 'AUTH_INTERACTION_EXPIRED':
      return 'EXPIRED';
    case 'AUTH_INTERACTION_REJECTED':
      return 'REJECTED';
    case 'AUTH_INTERACTION_FAILED':
      return 'FAILED';
    case 'TOO_MANY_ATTEMPTS':
      return 'TOO_MANY_ATTEMPTS';
    default:
      return null;
  }
}

function contractError(message: string): CollectionRevisionWireContractError {
  return new CollectionRevisionWireContractError(message);
}
